import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineStack,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris'

import { ExtensionInfo, useCustomerPortalExtensionInfo } from '../common/customer-portal'
import { adminDeepLink, useDialogActivator } from '../common/helpers'
import { HelpTitle } from '../common/HelpIcon'
import { mainThemeId, ThemeSettings } from '../common/themes'
import { customerPortalSettingsUrl } from '../common/urls'
import { InstallCustomerPortalModal } from '../settings-page/CustomerPortalThemeCard'

export const appEmbedInstallUrl = (themeId: string, appId: string) =>
  adminDeepLink(
    `/themes/${themeId}/editor?context=apps&template=index&activateAppId=${appId}/paywhirl-customer-portal`
  )

const AppEmbedStatus = ({ installed }: { installed: boolean }) => {
  return (
    <InlineStack gap="200" blockAlign="center" align="space-between">
      <HelpTitle title="App Embed">
        PayWhirl App Embed is used to integrate PayWhirl with your theme
      </HelpTitle>
      <Badge size="small" tone={installed ? 'success' : undefined}>
        {installed ? 'On' : 'Off'}
      </Badge>
    </InlineStack>
  )
}

const CustomerPortalExtensionStatus = ({ extensionInfo }: { extensionInfo: ExtensionInfo }) => {
  return (
    <InlineStack gap="200" blockAlign="center" align="space-between">
      <HelpTitle title="Customer Accounts Extension">
        Customer Accounts Extension integrates PayWhirl customer portal into Shopify's New Customer
        Accounts
      </HelpTitle>
      <Badge size="small" tone={extensionInfo.activations.length > 0 ? 'success' : undefined}>
        {extensionInfo.activations.length > 0 ? 'On' : 'Off'}
      </Badge>
    </InlineStack>
  )
}

export const CustomerPortalExtensionStatusCard = ({ settings }: { settings: ThemeSettings }) => {
  const extensionInfo = useCustomerPortalExtensionInfo()
  const activator = useDialogActivator()

  return (
    <Card>
      <BlockStack gap="200">
        {!extensionInfo && (
          <BlockStack gap="400">
            <SkeletonBodyText lines={1} />
            <SkeletonDisplayText size="medium" />
          </BlockStack>
        )}
        {extensionInfo && (
          <>
            <CustomerPortalExtensionStatus extensionInfo={extensionInfo} />
            <InlineStack gap="200">
              {extensionInfo.activations.length === 0 && (
                <>
                  <Button onClick={activator.show} variant="primary">
                    Install extension
                  </Button>
                </>
              )}
              {extensionInfo.activations.length > 0 && (
                <Button url={customerPortalSettingsUrl()}>Edit settings</Button>
              )}
            </InlineStack>
          </>
        )}
      </BlockStack>
      <InstallCustomerPortalModal activator={activator} legacy={false} themeSettings={settings} />
    </Card>
  )
}

export const AppEmbedStatusCard = ({ settings }: { settings: ThemeSettings }) => {
  const activator = useDialogActivator()

  return (
    <Card>
      <BlockStack gap="200">
        <AppEmbedStatus installed={settings.customerPortalInstalled} />
        <InlineStack gap="200">
          {!settings.customerPortalInstalled && (
            <>
              <Button onClick={activator.show} variant="primary">
                Install
              </Button>
              <InstallCustomerPortalModal
                activator={activator}
                legacy={true}
                themeSettings={settings}
              />
            </>
          )}
          {settings.customerPortalInstalled && (
            <Button
              onClick={() =>
                window.open(
                  appEmbedInstallUrl(mainThemeId(settings), shopify.config.apiKey),
                  '_blank'
                )
              }
            >
              Edit settings
            </Button>
          )}
        </InlineStack>
      </BlockStack>
    </Card>
  )
}
